import { Redirect, useLocalSearchParams } from "expo-router";

import { ResetPasswordScreen } from "@/interactors/ResetPasswordScreen";
import { VerifySignInWithEmailLinkScreen } from "@/interactors/VerifySignInWithEmailLinkScreen";
import { envVariables } from "@/utils/envVariables";
import { normalizeSearchParam } from "@/utils/routing";

/**
 * Single handler for all Firebase email action links. With a project-level custom
 * action URL configured, Firebase routes every email mode (resetPassword,
 * verifyEmail, recoverEmail, signIn) here, so we branch on the `mode` query param.
 * New modes plug in as additional branches.
 */
const AuthActionScreen = () => {
  const { mode, continueUrl, code, tenantId } = useLocalSearchParams<{
    mode: string;
    continueUrl: string;
    code: string;
    tenantId: string;
  }>();
  const normalizedMode = normalizeSearchParam(mode);
  const normalizedContinueUrl = normalizeSearchParam(continueUrl);
  const normalizedTenantId = normalizeSearchParam(tenantId);

  // Firebase routes every tenant's email links to the patient app's action URL.
  // When the link belongs to the admin tenant, forward it to the admin web app
  // (carrying all original query params) so the admin flow completes there.
  if (
    normalizedTenantId === envVariables.EXPO_PUBLIC_FIREBASE_TENANT_ID_ADMINS &&
    normalizedContinueUrl
  ) {
    const adminUrl = new URL(normalizedContinueUrl);
    for (const [key, value] of new URLSearchParams(window.location.search)) {
      if (key !== "continueUrl") {
        adminUrl.searchParams.set(key, value);
      }
    }
    window.location.href = adminUrl.toString();
    return null;
  }

  // Extract code from continueUrl if present (it's encoded as ?code=...)
  // Otherwise use the top-level code param (fallback when Firebase URL splits)
  let normalizedCode = normalizeSearchParam(code);
  if (normalizedContinueUrl && !normalizedCode) {
    try {
      const url = new URL(normalizedContinueUrl);
      const codeParam = url.searchParams.get("code");
      normalizedCode = normalizeSearchParam(codeParam ?? undefined);
    } catch {
      // If URL parsing fails, continue without code
    }
  }

  if (normalizedMode === "resetPassword") {
    return <ResetPasswordScreen />;
  }

  // Passwordless sign-in link. VerifySignInWithEmailLinkScreen completes it via isSignInWithEmailLink
  // against window.location.href (which carries the mode/oobCode params).
  if (normalizedMode === "signIn") {
    return <VerifySignInWithEmailLinkScreen code={normalizedCode} />;
  }

  return <Redirect href="/(unauthed)/auth" />;
};

export { AuthActionScreen };
